import React, { useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { gql, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import clientGraphql from '../../../common/apollo-graphql';
import CustomDialog from '../../common/components/CustomDialog';
import HookFormDateTimePicker from '../../common/components/hook-form/HookFormDateTimePicker';

const UpdateReferralSentDateDialog = ({ open, onClose, referral, isReceived = false }) => {
  const {
    control,
    getValues,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      sentdate: null
    }
  });

  const SAVE_REFERRAL = gql`
    mutation SaveReferral($params: String!) {
      save_referral(params: $params)
    }
  `;

  const [saveReferral] = useMutation(SAVE_REFERRAL, {
    client: clientGraphql
  });

  const parseSentDateTime = (value) => {
    if (!value) {
      return null;
    }

    const direct = dayjs(value);
    if (direct.isValid()) {
      return direct;
    }

    const normalized = dayjs(String(value).replace(' ', 'T'));
    if (normalized.isValid()) {
      return normalized;
    }

    return null;
  };

  useEffect(() => {
    if (open) {
      const rawSentAt = referral?.sent_at || referral?.referral?.sent_at || '';
      setValue('sentdate', parseSentDateTime(rawSentAt));
    }
  }, [open, referral, setValue]);

  const handleSave = async () => {
    try {
      const sentDateTime = getValues('sentdate');
      if (!sentDateTime || !sentDateTime.isValid()) {
        Swal.fire({
          icon: 'error',
          text: `${isReceived ? 'Date Received' : 'Date Sent'} is required.`,
          showConfirmButton: true,
        });
        return;
      }

      const payload = {
        uuid: referral?.id || referral?.referral?.id,
        status: referral?.status || referral?.referral?.status,
        sentdate: sentDateTime.format('YYYY-MM-DD HH:mm:ss'),
      };

      await saveReferral({
        variables: {
          params: JSON.stringify(payload),
        },
      });

      Swal.fire({
        icon: 'success',
        text: `${isReceived ? 'Date received' : 'Date sent'} updated!`,
        showConfirmButton: true,
      });

      onClose();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        text: error.message || `Error saving ${isReceived ? 'date received' : 'date sent'}!`,
        showConfirmButton: true,
      });
    }
  };

  return (
    <CustomDialog open={open} onClose={onClose}>
      <DialogTitle>{isReceived ? 'Update Date Received' : 'Update Date Sent'}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3} marginTop={3}>
          <HookFormDateTimePicker
            id="sentdate"
            name="sentdate"
            schema="sentdate"
            control={control}
            errors={errors}
            label={isReceived ? 'Date Received' : 'Date Sent'}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">Save</Button>
      </DialogActions>
    </CustomDialog>
  );
};

export default UpdateReferralSentDateDialog;
